/**
 * ActionButton Component - Wrapper around shadcn/ui Button for backward compatibility
 * Uses shadcn/ui Button component with improved styling and variants
 */
import { memo } from 'react';
import React from 'react';
import { Button as _Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type LegacyVariant = 'primary' | 'secondary' | 'danger' | 'success' | 'warning' | 'info' | 'default' | 'destructive' | 'outline' | 'ghost' | 'link';
type ButtonSize = 'default' | 'sm' | 'lg' | 'icon';

interface ActionButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label?: string;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  variant?: LegacyVariant;
  size?: ButtonSize;
  className?: string;
  children?: React.ReactNode;
}

type ShadcnVariant = 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
type ShadcnSize = 'default' | 'sm' | 'lg' | 'icon';

interface ButtonTypedProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ShadcnVariant;
  size?: ShadcnSize;
  asChild?: boolean;
}

const Button = _Button as React.ForwardRefExoticComponent<ButtonTypedProps & React.RefAttributes<HTMLButtonElement>>;

const variantMap: Record<LegacyVariant, string> = {
  primary: "default",
  secondary: "secondary",
  danger: "destructive",
  success: "default",
  warning: "default",
  info: "default",
  default: "default",
  destructive: "destructive",
  outline: "outline",
  ghost: "ghost",
  link: "link",
};

const ActionButton = memo(({
  label,
  icon: Icon,
  onClick,
  variant = "default",
  size = "default",
  className = "",
  children,
  ...props
}: ActionButtonProps) => {
  const mappedVariant = (variantMap[variant] || variant) as ShadcnVariant;

  return (
    <Button
      onClick={onClick}
      variant={mappedVariant}
      size={size}
      className={cn(
        "flex items-center space-x-2",
        className
      )}
      {...props}
    >
      {Icon && <Icon className="w-5 h-5" />}
      {children || (label && <span>{label}</span>)}
    </Button>
  );
});

ActionButton.displayName = 'ActionButton';

export default ActionButton;
